import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Loading from '../../../components/Loading';
import PaginationBar from '../../../components/PaginationBar/PaginationBar';
import usePermissions from '../../../components/Menu/MenuItems/hooks/usePermissions';
import ClienteStatusBadge from '../components/ClienteStatusBadge';
import { formatDocForDisplay } from '../../../utils/format';
import { formatPhoneForDisplay } from '../utils/format';
import { listCustomers } from '../../../services/customers/customersApi';
import { EmptyState } from '../../../styles/feedback/EmptyState';
import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
   PageHeaderActions,
} from '../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../styles/layout/Section';
import {
   ActionBar,
   ActionBarLeft,
   ActionBarRight,
} from '../../../styles/layout/ActionBar';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';
import { TableWrapper } from '../../../styles/table/TableWrapper';
import { Table } from '../../../styles/table/Table';
import { ClickableRow, TextLink } from '../../../styles/ui/Link';
import { CtaButton } from '../../../styles/ui/Button';

const ITEMS_PER_PAGE = 20;

// Ordenação default do backend (handoff 0029): order_by=name, order_dir=asc.
const DEFAULT_ORDER_BY = 'name';
const DEFAULT_ORDER_DIR = 'asc';
// Colunas ordenáveis = whitelist de order_by do backend.
const SORTABLE_KEYS = new Set(['name', 'created_at', 'status']);

// Filtros default. As chaves são snake_case = nomes dos query params da URL e do
// backend, evitando uma camada de tradução na página. A tradução para os args
// camelCase do service acontece em toServiceArgs().
const DEFAULT_FILTERS = {
   name: '',
   doc: '',
   refiltek_id: '',
   email: '',
   phone: '',
   person_type: '',
   status: '',
   created_from: '',
   created_to: '',
};

function readFiltersFromUrl(searchParams) {
   const next = { ...DEFAULT_FILTERS };
   Object.keys(DEFAULT_FILTERS).forEach((key) => {
      const value = searchParams.get(key);
      if (value !== null && value !== '') next[key] = value;
   });
   return next;
}

function readSortFromUrl(searchParams) {
   const orderBy = searchParams.get('order_by');
   if (orderBy && SORTABLE_KEYS.has(orderBy)) {
      return {
         key: orderBy,
         direction: searchParams.get('order_dir') === 'desc' ? 'desc' : 'asc',
      };
   }
   return { key: null, direction: null };
}

// Sem coluna ativa → ordenação default do backend.
function sortToParams(sort) {
   if (!sort.key) {
      return { orderBy: DEFAULT_ORDER_BY, orderDir: DEFAULT_ORDER_DIR };
   }
   return { orderBy: sort.key, orderDir: sort.direction };
}

// snake_case (URL/estado) → camelCase (args do service).
function toServiceArgs(filters, sort, page) {
   const { orderBy, orderDir } = sortToParams(sort);
   return {
      page,
      perPage: ITEMS_PER_PAGE,
      name: filters.name,
      doc: filters.doc,
      refiltekId: filters.refiltek_id,
      email: filters.email,
      phone: filters.phone,
      personType: filters.person_type,
      status: filters.status,
      createdFrom: filters.created_from,
      createdTo: filters.created_to,
      orderBy,
      orderDir,
   };
}

function formatDateBr(value) {
   if (!value) return '—';
   const date = new Date(value);
   if (Number.isNaN(date.getTime())) return '—';
   return date.toLocaleDateString('pt-BR');
}

export default function ClienteList() {
   const navigate = useNavigate();
   const { hasPermission } = usePermissions();
   const canCreate = hasPermission('customers:customer:create');

   const [searchParams, setSearchParams] = useSearchParams();
   // A URL é a fonte da verdade do filtro: se vier com query params (deep-link,
   // F5, voltar-do-detalhe), restauramos e buscamos no mount; URL limpa = tela
   // não dispara busca automática (operador precisa pesquisar). Leitura única.
   const hasInitialQueryParams = searchParams.toString() !== '';

   // ─── Estado inicial lido da URL (URL = fonte da verdade) ─────────────────
   // `filters` é o rascunho (ligado aos inputs); `appliedFilters` é o último
   // efetivamente pesquisado — usado em fetch, ordenação e paginação, pra evitar
   // operar com filtros editados mas ainda não confirmados.
   const [filters, setFilters] = useState(() => readFiltersFromUrl(searchParams));
   const [appliedFilters, setAppliedFilters] = useState(filters);
   // Ordenação segue o padrão de BillingCharges: clique no cabeçalho cicla
   // asc → desc → sem ordenação (volta ao default). É ação explícita do usuário,
   // então refaz a busca na hora (não fere o "sem auto-query" dos filtros).
   const [sortConfig, setSortConfig] = useState(() =>
      readSortFromUrl(searchParams),
   );
   const [currentPage, setCurrentPage] = useState(() => {
      const fromUrl = Number(searchParams.get('page'));
      return Number.isFinite(fromUrl) && fromUrl > 0 ? fromUrl : 1;
   });

   // ─── Resultados ──────────────────────────────────────────────────────────
   const [customers, setCustomers] = useState([]);
   const [totalPages, setTotalPages] = useState(1);
   const [totalCount, setTotalCount] = useState(0);
   const [isLoading, setIsLoading] = useState(false);

   const didInitialFetch = useRef(false);

   // ─── Serializa filtros + ordenação para a URL (sempre replace) ───────────
   // `applied=1` é um sentinela que marca "uma busca foi feita" — necessário
   // porque pesquisar SEM filtros não produz nenhum param, e aí a URL ficaria
   // limpa (indistinguível da abertura fria, que não deve buscar). Com ele, ao
   // voltar do detalhe a lista re-hidrata mesmo numa busca "todos os clientes".
   const commitToUrl = useCallback(
      (values, sort, page) => {
         const params = { applied: '1' };
         Object.keys(DEFAULT_FILTERS).forEach((key) => {
            if (values[key]) params[key] = values[key];
         });
         // Ordenação só vai pra URL quando difere do default.
         if (sort.key) {
            params.order_by = sort.key;
            params.order_dir = sort.direction;
         }
         if (page > 1) params.page = String(page);
         setSearchParams(params, { replace: true });
      },
      [setSearchParams],
   );

   // ─── Busca na API ─────────────────────────────────────────────────────────
   const fetchCustomers = useCallback(async (values, sort, page) => {
      setIsLoading(true);
      try {
         const result = await listCustomers(toServiceArgs(values, sort, page));
         setCustomers(result.customers);
         setTotalPages(result.totalPages);
         setTotalCount(result.total);
         setCurrentPage(result.page);
      } catch (err) {
         const apiMessage =
            err?.response?.data?.errors?.[0]?.message ??
            err?.response?.data?.message ??
            err?.message ??
            'Erro ao buscar clientes.';
         toast.error(apiMessage);
      } finally {
         setIsLoading(false);
      }
   }, []);

   // ─── Busca inicial — só quando a URL traz filtros (deep-link / F5 /
   // voltar-do-detalhe). URL limpa não dispara busca automática. ────────────
   useEffect(() => {
      if (didInitialFetch.current) return;
      didInitialFetch.current = true;
      if (!hasInitialQueryParams) return;
      fetchCustomers(appliedFilters, sortConfig, currentPage);
   }, [
      fetchCustomers,
      hasInitialQueryParams,
      appliedFilters,
      sortConfig,
      currentPage,
   ]);

   const updateField = (key, value) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
   };

   // ─── Pesquisa explícita (botão Pesquisar / Enter) ────────────────────────
   const handleSearch = (event) => {
      event.preventDefault();

      if (
         filters.created_from &&
         filters.created_to &&
         filters.created_from > filters.created_to
      ) {
         toast.error('A data inicial não pode ser maior que a data final.');
         return;
      }

      // Padrão BillingCharges: nova busca por filtros zera a ordenação.
      const resetSort = { key: null, direction: null };
      setAppliedFilters(filters);
      setSortConfig(resetSort);
      commitToUrl(filters, resetSort, 1);
      fetchCustomers(filters, resetSort, 1);
   };

   // Limpa tudo e esvazia a tela/URL — SEM disparar busca (padrão RoutingList).
   const handleClear = () => {
      setFilters(DEFAULT_FILTERS);
      setAppliedFilters(DEFAULT_FILTERS);
      setSortConfig({ key: null, direction: null });
      setCustomers([]);
      setTotalCount(0);
      setTotalPages(1);
      setCurrentPage(1);
      setSearchParams({}, { replace: true });
   };

   // Clique no cabeçalho — cicla asc → desc → default. Usa os filtros já
   // aplicados (não o rascunho) e volta pra página 1.
   const handleSort = (key) => {
      if (!SORTABLE_KEYS.has(key)) return;

      let nextSort;
      if (sortConfig.key === key) {
         if (sortConfig.direction === 'asc') {
            nextSort = { key, direction: 'desc' };
         } else {
            nextSort = { key: null, direction: null };
         }
      } else {
         nextSort = { key, direction: 'asc' };
      }

      setSortConfig(nextSort);
      commitToUrl(appliedFilters, nextSort, 1);
      fetchCustomers(appliedFilters, nextSort, 1);
   };

   const getSortIcon = (key) => {
      if (sortConfig.key !== key) return ' ⇅';
      return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
   };

   const handlePageChange = (page) => {
      commitToUrl(appliedFilters, sortConfig, page);
      fetchCustomers(appliedFilters, sortConfig, page);
   };

   const hasResults = customers.length > 0;

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Clientes</PageTitle>
               <PageSubtitle>
                  Cadastro central de clientes — dados e endereços
               </PageSubtitle>
            </PageHeaderText>
            <PageHeaderActions>
               {canCreate && (
                  <CtaButton
                     type="button"
                     onClick={() => navigate('/comercial/clientes/novo')}
                  >
                     + Novo cliente
                  </CtaButton>
               )}
            </PageHeaderActions>
         </PageHeader>

         {/* ─── Filtros ───────────────────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>Filtros</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <form onSubmit={handleSearch}>
                  <FormGrid>
                     <Field className="col-4">
                        <label htmlFor="filter-name">Nome / razão social</label>
                        <input
                           id="filter-name"
                           type="text"
                           placeholder="Parte do nome..."
                           value={filters.name}
                           onChange={(e) => updateField('name', e.target.value)}
                        />
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="filter-doc">CPF / CNPJ</label>
                        <input
                           id="filter-doc"
                           type="text"
                           inputMode="numeric"
                           placeholder="Com ou sem máscara..."
                           value={filters.doc}
                           onChange={(e) => updateField('doc', e.target.value)}
                        />
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="filter-refiltek">Código Refiltek</label>
                        <input
                           id="filter-refiltek"
                           type="text"
                           placeholder="Ex: RF-100"
                           value={filters.refiltek_id}
                           onChange={(e) =>
                              updateField('refiltek_id', e.target.value)
                           }
                        />
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="filter-email">E-mail</label>
                        <input
                           id="filter-email"
                           type="text"
                           placeholder="Parte do e-mail..."
                           value={filters.email}
                           onChange={(e) =>
                              updateField('email', e.target.value)
                           }
                        />
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="filter-phone">Telefone</label>
                        <input
                           id="filter-phone"
                           type="text"
                           inputMode="numeric"
                           placeholder="DDD + número..."
                           value={filters.phone}
                           onChange={(e) =>
                              updateField('phone', e.target.value)
                           }
                        />
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="filter-person-type">Tipo de pessoa</label>
                        <select
                           id="filter-person-type"
                           value={filters.person_type}
                           onChange={(e) =>
                              updateField('person_type', e.target.value)
                           }
                        >
                           <option value="">Todos</option>
                           <option value="PF">PF — Pessoa Física</option>
                           <option value="PJ">PJ — Pessoa Jurídica</option>
                        </select>
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="filter-status">Status</label>
                        <select
                           id="filter-status"
                           value={filters.status}
                           onChange={(e) =>
                              updateField('status', e.target.value)
                           }
                        >
                           <option value="">Todos</option>
                           <option value="active">Ativo</option>
                           <option value="inactive">Inativo</option>
                        </select>
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="filter-created-from">Cadastrado de</label>
                        <input
                           id="filter-created-from"
                           type="date"
                           value={filters.created_from}
                           onChange={(e) =>
                              updateField('created_from', e.target.value)
                           }
                        />
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="filter-created-to">Cadastrado até</label>
                        <input
                           id="filter-created-to"
                           type="date"
                           value={filters.created_to}
                           onChange={(e) =>
                              updateField('created_to', e.target.value)
                           }
                        />
                     </Field>
                  </FormGrid>

                  <ActionBar style={{ marginTop: 16 }}>
                     <ActionBarLeft />
                     <ActionBarRight>
                        <TextLink
                           as="button"
                           type="button"
                           onClick={handleClear}
                           disabled={isLoading}
                        >
                           Limpar
                        </TextLink>
                        <CtaButton type="submit" disabled={isLoading}>
                           Pesquisar
                        </CtaButton>
                     </ActionBarRight>
                  </ActionBar>
               </form>
            </SectionBody>
         </Section>

         {/* ─── Resultados ────────────────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>
                  Resultados{totalCount ? ` (${totalCount})` : ''}
               </SectionTitle>
            </SectionHeader>
            <SectionBody>
               {hasResults ? (
                  <>
                     <TableWrapper>
                        <Table>
                           <thead>
                              <tr>
                                 <th
                                    onClick={() => handleSort('name')}
                                    style={{ cursor: 'pointer' }}
                                 >
                                    Nome{getSortIcon('name')}
                                 </th>
                                 <th>CPF/CNPJ</th>
                                 <th>Telefone</th>
                                 <th
                                    onClick={() => handleSort('created_at')}
                                    style={{ cursor: 'pointer' }}
                                 >
                                    Cadastrado em{getSortIcon('created_at')}
                                 </th>
                                 <th
                                    className="center"
                                    onClick={() => handleSort('status')}
                                    style={{ cursor: 'pointer' }}
                                 >
                                    Status{getSortIcon('status')}
                                 </th>
                              </tr>
                           </thead>
                           <tbody>
                              {customers.map((customer) => (
                                 <ClickableRow
                                    key={customer.id}
                                    onClick={() =>
                                       navigate(
                                          `/comercial/clientes/${customer.id}`,
                                       )
                                    }
                                 >
                                    <td>{customer.name}</td>
                                    <td>{formatDocForDisplay(customer.doc)}</td>
                                    <td>
                                       {formatPhoneForDisplay(customer.phone)}
                                    </td>
                                    <td>{formatDateBr(customer.created_at)}</td>
                                    <td className="center">
                                       <ClienteStatusBadge
                                          value={customer.status}
                                       />
                                    </td>
                                 </ClickableRow>
                              ))}
                           </tbody>
                        </Table>
                     </TableWrapper>

                     <PaginationBar
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        disabled={isLoading}
                     />
                  </>
               ) : (
                  <EmptyState
                     title="Nenhum cliente encontrado"
                     description="Ajuste os filtros e clique em Pesquisar, ou cadastre um novo cliente."
                  />
               )}
            </SectionBody>
         </Section>
      </Container>
   );
}
